import { useCallback, useEffect, useState } from 'react'
import { api, dropNulls } from './api'
import type { AreaUnit, SprayLog } from './types'

export type SprayProductInput = {
  productName: string
  /** The registered product picked from the registry, if any. */
  productId?: string
  pcpNumber?: string
  quantityUsed: number
  quantityUnit: string
  rateApplied?: number
  rateUnit?: string
  labelFollowed?: boolean
  eahd?: string
  phiDays?: number
}

export type CreateSprayLogInput = {
  fieldId: string
  seasonYear: number
  appliedOn: string
  applicationMethod: string
  areaTreated?: number | null
  areaUnit?: AreaUnit | null
  weatherConditions?: string | null
  applicatorName?: string | null
  customInvoiceAttached?: boolean
  products: SprayProductInput[]
}

export type UpdateSprayLogInput = Partial<Omit<CreateSprayLogInput, 'fieldId' | 'seasonYear'>>

export function useSprayLogs(
  orgId: string | undefined,
  farmId: string | undefined,
  fieldId: string | undefined,
  seasonYear: number,
) {
  const [logs, setLogs] = useState<SprayLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const base = orgId && farmId ? `/orgs/${orgId}/farms/${farmId}/spray-logs` : null

  const loadLogs = useCallback(async () => {
    if (!base || !fieldId) return
    setLoading(true)
    setError(null)
    try {
      // The list endpoint returns summaries; products come with each log's detail.
      const { data } = await api.getPage<SprayLog>(`${base}?fieldId=${fieldId}&seasonYear=${seasonYear}`)
      setLogs(await Promise.all(data.map((log) => api.get<SprayLog>(`${base}/${log.id}`))))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load spray logs.')
    } finally {
      setLoading(false)
    }
  }, [base, fieldId, seasonYear])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  async function addLog(input: CreateSprayLogInput) {
    if (!base) return
    const created = await api.post<SprayLog>(base, dropNulls(input))
    setLogs((prev) => [created, ...prev])
    return created
  }

  async function updateLog(id: string, patch: UpdateSprayLogInput) {
    if (!base) return
    const updated = await api.patch<SprayLog>(`${base}/${id}`, patch)
    setLogs((prev) => prev.map((l) => (l.id === id ? updated : l)))
    return updated
  }

  return { logs, loading, error, addLog, updateLog }
}
